import { getWorkflow } from '@jira/project-settings__workflow-services-document/src/getApiPrefix.tsx';
import type { ApiGetWorkflowResponseWithUsages } from '@jira/project-settings__workflow-types/src/workflowTypes.types.tsx';
import type { GraphQLError } from 'graphql';
import { setMark } from '@atlassian/jira-common-performance/src/marks.tsx';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
// eslint-disable-next-line jira/import-whitelist
import {
	prefetchApiReadyMark,
	prefetchApiStartMark,
} from '@atlassian/jira-providers-spa-apdex-analytics/src/marks.tsx';
import { createResourceError } from '@atlassian/jira-router-resources-utils/src/utils/create-resource-error/index.tsx';
import type { ResourceStoreContext, RouterDataContext } from '@atlassian/react-resource-router';
import { QUERY } from './gql.tsx';

type ProjectResponse = {
	data?: {
		jira_projectByIdOrKey?: { projectId: string } | null;
	} | null;
	errors?: ReadonlyArray<GraphQLError>;
};

export const fetchWorkflowDocument = async (
	routerContext: RouterDataContext,
	{ tenantContext }: ResourceStoreContext,
	isProjectScoped: boolean,
): Promise<ApiGetWorkflowResponseWithUsages> => {
	const { isAnonymous, cloudId } = tenantContext;
	const { match } = routerContext;

	if (isAnonymous) {
		throw new Error("Workflows can't be edited in anonymous mode.");
	}
	if (!match.params.workflowId && !match.params.projectKey && !match.params.issueTypeId) {
		throw new Error('Workflow ID, project Key, or issue type ID is not defined.');
	}

	try {
		setMark(prefetchApiStartMark);

		let workflowDocument: ApiGetWorkflowResponseWithUsages | null = null;
		if (match.params.workflowId) {
			workflowDocument = await getWorkflow(
				cloudId,
				{
					workflowId: match.params.workflowId,
				},
				{ checkForDrafts: !isProjectScoped },
			);
		} else if (match.params.projectKey && match.params.issueTypeId) {
			const { data, errors } = await performPostRequest<ProjectResponse>(
				'/gateway/api/graphql?operation=JiraProjectIdForWorkflowDocument',
				{
					method: 'POST',
					headers: {
						'content-type': 'application/json',
						'X-ExperimentalApi': 'JiraProject, projectStyle',
					},
					body: JSON.stringify({
						query: QUERY,
						variables: {
							cloudId,
							projectIdOrKey: match.params.projectKey,
						},
					}),
				},
			);
			if (errors) {
				throw errors;
			}
			if (!data || !data.jira_projectByIdOrKey?.projectId) {
				throw new Error('Project ID is not defined.');
			}

			workflowDocument = await getWorkflow(
				cloudId,
				{
					projectAndIssueType: {
						projectId: String(data.jira_projectByIdOrKey.projectId),
						issueTypeId: match.params.issueTypeId,
					},
				},
				{ checkForDrafts: !isProjectScoped },
			);
		} else {
			throw new Error('Project workflow context is not defined.');
		}

		if (!workflowDocument) {
			throw new Error('Workflow ID, project ID, or issue type ID is not defined.');
		}

		setMark(prefetchApiReadyMark);
		return workflowDocument;
	} catch (error: unknown) {
		throw createResourceError(
			{
				component: 'spa.view.routes.admin-pages.workflows.workflow-document.getData',
				message: 'An error was thrown while prefetching workflow document data',
				name: 'ResourceError',
			},
			// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions
			error as any,
		);
	}
};
